/**
 * The metrics: what this template asks a run to produce, and what it does when
 * it cannot find one of them.
 *
 * One thing on this page is editable: which metrics the recipe asks for at all
 * (DOP-462). The checkboxes used to be checked-and-disabled because nothing
 * stored them; `figure_enabled` on the template PATCH does, so they are live.
 * The missing-figure policy and the cadence moved to the dashboard's Settings
 * tab in DOP-480 — this page is which metrics, not how the template is run.
 *
 * The checkboxes are read from the `template` prop on every render, never
 * copied into local state. A local copy would go on showing the click after a
 * save failed, which is the one moment the setting has to be believable.
 *
 * A derived figure is only as available as its inputs, so one whose formula
 * reads a switched-off figure is shown off and frozen, with the input that
 * blocks it named. The rule is computed here from the spec the page already
 * has — the server applies the same one when it runs.
 *
 * A row is a NAME and nothing else (DOP-458): "Revenue", not "Revenue (LTM)".
 * The qualifier is split off and NOT shown — the operator's call: which period
 * a figure covers is captured where the number lands, on the run's ledger and
 * in the summary, not on the list of what the fund tracks. The formula and the
 * document search order went for the same reason: how a figure is found is the
 * run's business, shown on the run's own ledger next to the number it produced.
 *
 * Groups come from the spec, never from a list in here: the default template
 * carries Financials and Covenants today, and a template stored when Leverage
 * and Adjustments were still in the default must still render its own groups.
 */

import React, { useMemo } from "react";
import type {
	MonitoringFigureSpec,
	MonitoringTemplate,
} from "../../types/monitoring";
import { isFigureEnabled } from "../../types/monitoring";

/**
 * The heading a group key is written with, where it differs from the key.
 *
 * A lookup, NOT the list of groups to render: a key with no entry here (one a
 * newer template introduces) still gets a heading, from its own key.
 */
const GROUP_HEADINGS: Readonly<Record<string, string>> = {
	financials: "Financials",
	leverage: "Leverage & coverage",
	covenants: "Covenants",
	adjustments: "Adjustments",
};

/** The sheets the export always writes, in tab order. */
/** The heading order for the groups a spec happens to carry. */
const GROUP_ORDER: ReadonlyArray<string> = [
	"financials",
	"leverage",
	"covenants",
	"adjustments",
];

/** `cash_conversion` → `Cash conversion`, for a group nothing has named yet. */
const readableKey = (key: string): string => {
	const spaced = key.replace(/_/g, " ");
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const groupHeading = (key: string): string =>
	GROUP_HEADINGS[key] ?? readableKey(key);

/**
 * A figure's name and the qualifier that narrows it, as two separate strings.
 *
 * `qualifier` is what the server sends for a template written after DOP-458.
 * Older templates carry it inside the label ("Revenue (LTM)", "EBITDA
 * (certified)"), so a trailing parenthetical is split off here — otherwise the
 * same template would read differently depending on when it was created.
 */
export const splitQualifier = (
	figure: Pick<MonitoringFigureSpec, "label" | "qualifier">,
): { name: string; qualifier: string | null } => {
	const trailing = /^(.*\S)\s*\(([^()]+)\)\s*$/.exec(figure.label);
	const name = (trailing ? trailing[1] : figure.label).trim();
	const fromLabel = trailing ? trailing[2].trim() : null;
	const explicit = figure.qualifier?.trim();
	return { name, qualifier: explicit ? explicit : fromLabel };
};

/**
 * The inputs a derived figure needs that are switched off, by their bare names.
 *
 * Only keys the spec actually carries are considered: a formula naming a figure
 * this template does not have is a gap in the recipe, not a reason to freeze a
 * row the user could otherwise fix. Empty for an input or covenant figure —
 * those depend on documents, not on other rows.
 */
export const blockingInputs = (
	figure: MonitoringFigureSpec,
	byKey: ReadonlyMap<string, MonitoringFigureSpec>,
): string[] => {
	if (figure.kind !== "derived") return [];
	// Follows the chain, the way the server's effective-figure rule does: net
	// leverage reads net debt, which reads total cash, so switching total cash
	// off blocks both — and the hint names the switch that was flipped ("needs
	// Total Cash"), not the intermediate figure nobody touched.
	const roots: string[] = [];
	const seen = new Set<string>([figure.key]);
	const walk = (current: MonitoringFigureSpec) => {
		for (const key of current.inputs) {
			const input = byKey.get(key);
			if (input === undefined || seen.has(key)) continue;
			seen.add(key);
			if (!isFigureEnabled(input)) {
				roots.push(splitQualifier(input).name);
			} else if (input.kind === "derived") {
				walk(input);
			}
		}
	};
	walk(figure);
	return roots;
};

interface FigureRowProps {
	figure: MonitoringFigureSpec;
	/** Names of the switched-off figures this one is computed from. */
	blockedBy: ReadonlyArray<string>;
	/** This row's save is in the air: frozen until the server answers. */
	saving: boolean;
	onToggle?: (key: string, enabled: boolean) => void;
}

const FigureRow: React.FC<FigureRowProps> = React.memo(
	({ figure, blockedBy, saving, onToggle }) => {
		const { name } = splitQualifier(figure);
		const blocked = blockedBy.length > 0;
		// A blocked derived figure reads as OFF whatever the stored flag says:
		// nothing can compute it, so a tick would claim a number the run will
		// never produce.
		const checked = isFigureEnabled(figure) && !blocked;
		return (
			<li className="flex items-center gap-2 py-1">
				<input
					type="checkbox"
					className="rounded border-gray-300 text-[#008782] focus:ring-[#008782] disabled:cursor-not-allowed disabled:opacity-60"
					checked={checked}
					// No `onToggle` at all means the caller has nothing to save to,
					// so the box goes back to being a statement of fact.
					disabled={blocked || saving || !onToggle}
					readOnly={!onToggle}
					aria-label={figure.label}
					onChange={
						onToggle
							? (event) => onToggle(figure.key, event.target.checked)
							: undefined
					}
				/>
				<span className="flex min-w-0 flex-1 items-center gap-2">
					<span className="text-[13.5px] text-[#1F2937]">{name}</span>
					{blocked && (
						<span className="text-sm text-text-muted">
							needs {blockedBy.join(", ")}
						</span>
					)}
				</span>
			</li>
		);
	},
);
FigureRow.displayName = "MonitoringFigureRow";

interface FigureGroupBlockProps {
	groupKey: string;
	label: string;
	figures: MonitoringFigureSpec[];
	byKey: ReadonlyMap<string, MonitoringFigureSpec>;
	savingKeys: ReadonlySet<string>;
	onToggle?: (key: string, enabled: boolean) => void;
}

const FigureGroupBlock: React.FC<FigureGroupBlockProps> = ({
	groupKey,
	label,
	figures,
	byKey,
	savingKeys,
	onToggle,
}) => (
	<div>
		<div
			className="flex items-center justify-between py-1.5"
			data-testid={`metric-group-${groupKey}`}
		>
			<span className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#004C4B]">{label}</span>
			<span className="text-xs text-[#9a9ea3]">{figures.length}</span>
		</div>
		<ul>
			{figures.map((figure) => (
				<FigureRow
					key={figure.key}
					figure={figure}
					blockedBy={blockingInputs(figure, byKey)}
					saving={savingKeys.has(figure.key)}
					onToggle={onToggle}
				/>
			))}
		</ul>
	</div>
);

interface MetricsViewProps {
	template: MonitoringTemplate;
	/**
	 * Called with the metric the user included or excluded (DOP-462). The page
	 * saves it. Omitted, the checkboxes fall back to read-only.
	 */
	onToggleFigure?: (key: string, enabled: boolean) => void;
	/** The figure keys whose save is in the air; those rows are frozen. */
	savingFigureKeys?: ReadonlySet<string>;
}

const NO_KEYS: ReadonlySet<string> = new Set<string>();

const MetricsView: React.FC<MetricsViewProps> = ({
	template,
	onToggleFigure,
	savingFigureKeys = NO_KEYS,
}) => {
	// Every figure by key, so a derived row can ask whether the figures its
	// formula reads are still switched on.
	const byKey = useMemo(
		() =>
			new Map(template.figure_spec.map((figure) => [figure.key, figure] as const)),
		[template.figure_spec],
	);
	// Sorted by the spec's own `sort_order` so the page shows the metrics in the
	// order the workbook writes them, whatever order the API happened to serve.
	// The groups are the ones the SPEC carries, in the known heading order with
	// anything unrecognised after it — a template written before a group was
	// retired keeps its rows instead of losing them to a hardcoded list.
	const groups = useMemo(() => {
		const grouped = new Map<string, MonitoringFigureSpec[]>();
		for (const figure of [...template.figure_spec].sort(
			(a, b) => a.sort_order - b.sort_order,
		)) {
			const bucket = grouped.get(figure.group);
			if (bucket) bucket.push(figure);
			else grouped.set(figure.group, [figure]);
		}
		const rank = (key: string): number => {
			const index = GROUP_ORDER.indexOf(key);
			return index === -1 ? GROUP_ORDER.length : index;
		};
		return [...grouped.entries()]
			.map(([key, figures]) => ({ key, figures }))
			.sort((a, b) => rank(a.key) - rank(b.key));
	}, [template.figure_spec]);

	return (
		<section className="min-w-0 rounded-xl border border-gray-200 bg-white p-5">
			<h2 className="text-[15px] font-semibold text-[#111111]">What Doppler reads each period</h2>
			<p className="mt-1 text-[13px] text-[#6B7280]">
				Every figure the workbook gets for each borrower. Untick one to stop reading it; a
				computed figure needs its inputs.
			</p>
			<div className="mt-3 grid gap-x-6 gap-y-3 md:grid-cols-2">
				{groups.map(({ key, figures }) => (
					<FigureGroupBlock
						key={key}
						groupKey={key}
						label={groupHeading(key)}
						figures={figures}
						byKey={byKey}
						savingKeys={savingFigureKeys}
						onToggle={onToggleFigure}
					/>
				))}
			</div>
		</section>
	);
};

export default MetricsView;
